import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Points,
  ShaderLib,
  ShaderMaterial,
  Vector3,
} from 'three';

import geometryVertexFragmentShader from '../../../../static/glsl/points/geometry-vertex.frag';
import { renderOrders } from '../../render-orders';

// Kept for backwards-compatibility with existing call sites (geometry.ts, volume-boundary-polygon.ts).
export enum SphereSprite {
  Dot = 'geometry_dot.png',
  DotGray = 'geometry_dot_gray.png',
  DotPlain = 'plain_dot.png',
}

export interface SphereOptions {
  color: number;
}

const GHOST_COLOR = new Color(0x888888);
const HOVER_COLOR = new Color(0x88ff88);
const POINT_SIZE = 14;

/**
 * A single vertex rendered as a round point using a shader — no texture file required.
 * Used to show editable vertices and midpoint ghost handles on geometry assets (line / area).
 *
 * Replaces the previous Sprite-based implementation that depended on asynchronously
 * loaded texture files (geometry_dot.png / geometry_dot_gray.png), which caused the
 * vertices to render as white squares whenever the textures were unavailable.
 */
export class Sphere extends Points {
  get isGhost(): boolean {
    return this._isGhost;
  }

  private _isGhost: boolean;
  private _baseColor: Color;

  constructor(
    point: Vector3,
    sphereSprite: SphereSprite = SphereSprite.Dot,
    sphereOptions: SphereOptions = { color: 0xffffff },
  ) {
    const isGhost = sphereSprite === SphereSprite.DotGray;
    const baseColor = isGhost ? GHOST_COLOR.clone() : new Color(sphereOptions.color);

    // Single vertex at the local origin; the actual position in the scene is
    // controlled via this.position so that getWorldPosition() works correctly.
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute([0, 0, 0], 3));

    super(
      geometry,
      new ShaderMaterial({
        transparent: true,
        depthTest: false,
        depthWrite: false,
        uniforms: {
          size: { value: POINT_SIZE },
          scale: { value: 1 },
          color: { value: baseColor.clone() },
        },
        vertexShader: ShaderLib.points.vertexShader,
        fragmentShader: geometryVertexFragmentShader,
      }),
    );

    this.position.copy(point);
    this.renderOrder = renderOrders.pickers.pickingSpere;
    this._isGhost = isGhost;
    this._baseColor = baseColor.clone();
  }

  dispose() {
    this.geometry.dispose();
    (this.material as ShaderMaterial).dispose();
  }

  /** No-op: shader renders at a fixed pixel size; no billboard update is required. */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onAnimate() {}

  onHover() {
    (this.material as ShaderMaterial).uniforms.color.value = HOVER_COLOR.clone();
    (this.material as ShaderMaterial).uniformsNeedUpdate = true;
  }

  onNotHover() {
    (this.material as ShaderMaterial).uniforms.color.value = this._baseColor.clone();
    (this.material as ShaderMaterial).uniformsNeedUpdate = true;
  }

  setColor(hex: number) {
    (this.material as ShaderMaterial).uniforms.color.value = new Color(hex);
    (this.material as ShaderMaterial).uniformsNeedUpdate = true;
  }

  /**
   * Adjusts the rendered point size by a multiplier, e.g. 1.5 on hover.
   * Replaces the billboard scale modifier from the previous Sprite-based implementation.
   */
  setScaleModifier(modifier: number) {
    (this.material as ShaderMaterial).uniforms.size.value = POINT_SIZE * modifier;
    (this.material as ShaderMaterial).uniformsNeedUpdate = true;
  }
}
